import React, { useContext, useState, useEffect } from "react";

import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";

//   icon
import { BiMinusCircle } from "react-icons/bi";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { SelectAcademicYear, SelectSchool } from "../../Function/DynamicSelect";
import { GENERATE_NEW_CLASS_ATTENDED } from "../../Schema/Student";
import DatePickerForDialog from "../Include/SelecteDatePicker/DatePickerForDialog";
import moment from "moment";

export default function GenerateNewClassAttended({
  open,
  handleClose,
  setRefetch,
}) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [oldAcademicYearVal, setOldAcademicYearVal] = useState({
    id: "",
    title: "",
  });
  const [newAcademicYearVal, setNewAcademicYearVal] = useState({
    id: "",
    title: "",
  });
  const [schoolVal, setSchoolVal] = useState({ id: "", title: "" });

  //======================== Create User =========================
  const [generateNewClassAttended] = useMutation(GENERATE_NEW_CLASS_ATTENDED, {
    onCompleted: ({ generateNewClassAttended }) => {
      // console.log("generateNewClassAttended", generateNewClassAttended);
      setLoading(false);
      if (generateNewClassAttended?.is_success === true) {
        setAlert(true, "success", generateNewClassAttended?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", generateNewClassAttended?.message);
        // console.log("error:::", generateNewClassAttended?.message);
        handleClose();
      }
    },
    onError: (error) => {
      setLoading(false);
      if (error?.message?.message_kh && error?.message?.message_en) {
        setAlert(true, "error", error?.message);
      } else {
        setAlert(true, "error", {
          message_kh: error?.message,
          message_en: error?.message,
        });
      }
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    join_date: Yup.string().required(t("require")),
    new_academic_year_id: Yup.string().required(t("require")),
    old_academic_year_id: Yup.string().required(t("require")),
    school_id: Yup.string().required(t("require")),
  });

  const formik = useFormik({
    initialValues: {
      join_date: moment(),
      new_academic_year_id: "",
      old_academic_year_id: "",
      school_id: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      // console.log("values:::", values);
      setLoading(true);

      generateNewClassAttended({
        variables: {
          input: {
            join_date: values?.join_date,
            new_academic_year_id: values?.new_academic_year_id,
            old_academic_year_id: values?.old_academic_year_id,
            school_id: values?.school_id,
          },
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    setFieldValue("school_id", schoolVal?.id ?? "");
  }, [schoolVal?.id]);
  useEffect(() => {
    setFieldValue("old_academic_year_id", oldAcademicYearVal?.id ?? "");
  }, [oldAcademicYearVal?.id]);
  useEffect(() => {
    setFieldValue("new_academic_year_id", newAcademicYearVal?.id ?? "");
  }, [newAcademicYearVal?.id]);

  useEffect(() => {
    resetForm();
    setSchoolVal({ id: "", title: "" });
    setOldAcademicYearVal({ id: "", title: "" });
    setNewAcademicYearVal({ id: "", title: "" });
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("generate_new_class")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("old_academic_year")}
                </Typography>
                <SelectAcademicYear
                  selectedVal={oldAcademicYearVal}
                  setSelectedVal={setOldAcademicYearVal}
                />
                {!!errors.old_academic_year_id &&
                  touched.old_academic_year_id && (
                    <FormHelperText error id="outlined-adornment-password">
                      {errors.old_academic_year_id}
                    </FormHelperText>
                  )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("new_academic_year")}
                </Typography>
                <SelectAcademicYear
                  selectedVal={newAcademicYearVal}
                  setSelectedVal={setNewAcademicYearVal}
                />
                {!!errors.new_academic_year_id &&
                  touched.new_academic_year_id && (
                    <FormHelperText error id="outlined-adornment-password">
                      {errors.new_academic_year_id}
                    </FormHelperText>
                  )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("school")}
                </Typography>
                <SelectSchool
                  selectedVal={schoolVal}
                  setSelectedVal={setSchoolVal}
                />
                {!!errors.school_id && touched.school_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.school_id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("join_date")}
                </Typography>
                <DatePickerForDialog
                  date={values?.join_date}
                  setDate={(e) => setFieldValue("join_date", e)}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
              // disabled={csvData.length >= 1 ? true : false}
            >
              {t("btn-create")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
